// Service record data for Terence Peter Field — Royal Navy 1962–1978
// Built for Dad to enjoy. Photos sourced from Wikimedia Commons / IWM and
// served as static files from /images.
//
// Strict rule for inclusion:
//   1. Must be the actual ship / establishment Dad served in (no sister ships,
//      no tangential context shots).
//   2. No third-party watermarks visible.
//   3. Caption must be honest about what is shown.

const IMG_BASE = "/images";

function safeName(url) {
  const last = url.split("/").pop();
  const decoded = decodeURIComponent(last);
  return decoded.replace(/[^A-Za-z0-9._-]+/g, "_");
}

export const SAILOR = {
  fullName: "Terence Peter Field",
  born: "01 July 1946",
  serviceYears: "1962 – 1978",
  finalRank: "Chief Petty Officer Mechanician First Class",
  branch: "Marine Engineering — Submarine Service",
  // Dad's own photograph from his time at HMS Ganges, aged 16, in 1962.
  // The cap tally clearly reads "H.M.S. GANGES".
  portrait: `${IMG_BASE}/Dad_TPF_HMS_Ganges_1962_portrait.jpg`,
  portraitCaption: "Dad, aged 16 — boy entrant, HMS Ganges, 1962.",
};

export const HERO_IMAGE =
  "/images/hero-ocean.jpg";

const W = (path) =>
  `${IMG_BASE}/${safeName(`https://upload.wikimedia.org/wikipedia/commons/${path}`)}`;
const EN = (path) =>
  `${IMG_BASE}/${safeName(`https://upload.wikimedia.org/wikipedia/en/${path}`)}`;

export const SHIPS = [
  {
    slug: "hms-ganges",
    name: "HMS Ganges",
    pennant: "Shore Establishment",
    dates: "1962 – 1963",
    type: "Boys' Training Establishment",
    location: "Shotley, Suffolk, England",
    role: "Junior Seaman / Boy Entry",
    // Dad's actual 1962 portrait — taken at HMS Ganges, aged 16, cap tally
    // clearly visible. Shown as a feature section on this ship's page.
    dadPortrait: {
      src: `${IMG_BASE}/Dad_TPF_HMS_Ganges_1962_portrait.jpg`,
      caption: "Dad, aged 16 — boy entrant, HMS Ganges, 1962.",
    },
    images: [
      { src: W("5/5a/HMS_Ganges_mast.jpg"), caption: "The famous 143-foot mast at HMS Ganges, Shotley." },
      { src: `${IMG_BASE}/Dad_TPF_HMS_Ganges_1962_portrait.jpg`, caption: "Dad's own portrait — aged 16, in cap tally H.M.S. GANGES, 1962.", isDad: true },
      { src: W("0/0c/Boys_discharged_to_HMS_Ganges_as_2nd_class_boys_%284322191042%29.jpg"), caption: "Boys discharged to HMS Ganges as 2nd Class Boys — exactly the path Dad took." },
      { src: W("1/14/HMS_Adventure_and_HMS_Ganges_Flickr_4343898162_d5b0c32a51_o.jpg"), caption: "HMS Adventure alongside HMS Ganges." },
      { src: W("7/7d/HMS_Ganges_%28former%29_-_geograph.org.uk_-_839260.jpg"), caption: "The former HMS Ganges training establishment at Shotley." },
    ],
    summary:
      "The legendary Royal Navy training establishment at Shotley, where generations of boy entrants were forged into sailors beneath the famous 143-foot mast.",
    history: [
      "HMS Ganges traces its lineage to a 4th-rate ship of the line launched in Bombay in 1821 — the last sailing battleship in the Royal Navy.",
      "From 1905 it became a shore-based boys' training establishment at Shotley Point, on the confluence of the Stour and Orwell rivers.",
      "By the 1960s, Ganges was the largest and most spartan of the Royal Navy's training establishments, putting some 2,000 boys aged 15 to 17 through 12 months of seamanship, gunnery, signals and physical training.",
      "Cadets famously climbed the 143-foot mast in the parade ground; the 'button boy' who stood on the truck at the top during ceremonial occasions became part of naval folklore.",
      "The establishment closed in 1976, three years after Dad finished his time there.",
    ],
    role_detail:
      "As a 16-year-old new entry in 1962, Dad would have gone through the foundational Part I training — kit musters, knot-tying, boat-pulling on the river, classroom instruction, and the first steps on the long road to Marine Engineering.",
  },
  {
    slug: "hms-tenby",
    name: "HMS Tenby",
    pennant: "F65",
    dates: "August 1963 – October 1963",
    type: "Whitby-class (Type 12) Anti-Submarine Frigate",
    location: "Home Fleet",
    role: "Junior Rating — Sea Training",
    images: [
      { src: W("c/c7/HMS_Tenby.jpg"), caption: "HMS Tenby (F65)." },
      { src: W("b/ba/HMS_Tenby_%28F65%29%2C_1963.jpg"), caption: "HMS Tenby photographed in 1963 — the year Dad joined her." },
      { src: W("9/9a/HMS_Tenby_%2811114628593%29.jpg"), caption: "HMS Tenby underway." },
      { src: W("0/0c/HMS_Tenby%2C_Dartmouth%2C_1972.jpg"), caption: "HMS Tenby at Dartmouth, 1972." },
    ],
    summary:
      "A first sea draft aboard a steam-powered Type 12 frigate — the class that defined post-war Royal Navy anti-submarine warfare.",
    history: [
      "HMS Tenby (F65) was a Whitby-class anti-submarine frigate, launched at Cammell Laird on 4 October 1953 and commissioned on 18 December 1957.",
      "Displacing 2,150 tons standard, she was 370 feet long and powered by two Y-100 geared steam turbines giving 30 knots — fast enough to chase the new generation of Soviet submarines.",
      "Armed with twin 4.5-inch Mk.6 guns forward and two Limbo Mk.10 anti-submarine mortars aft, the Whitby class set the template for every British frigate that followed.",
      "Tenby served as the Dartmouth Training Ship and with the Fishery Protection Squadron before being placed in reserve in 1972 and broken up in 1977.",
    ],
    role_detail:
      "A short two-month draft suggests Dad joined Tenby for a familiarisation cruise — his first taste of life at sea, of watches, mess decks, and the constant hum of the engine room that would become his career.",
  },
  {
    slug: "hms-centaur",
    name: "HMS Centaur",
    pennant: "R06",
    dates: "October 1963 – October 1965",
    type: "Centaur-class Light Fleet Aircraft Carrier",
    location: "East of Suez — Far East, Aden, Mediterranean",
    role: "Junior Marine Engineer",
    images: [
      { src: W("5/58/HMS_Centaur_%28R06%29_1962.jpg"), caption: "HMS Centaur (R06) photographed in 1962." },
      { src: `${IMG_BASE}/Dad_SS_Lakonia_Whirlwind977_1963.jpg`, caption: "SS Lakonia adrift and still smouldering off Madeira, 22 December 1963 — with one of Centaur's Whirlwind helicopters (tail 977) overhead. From Dad's own collection.", isDad: true },
      { src: W("9/93/HMS_Centaur_1965.jpg"), caption: "HMS Centaur in 1965 — the year Dad's draft ended." },
      { src: W("9/9c/HMS_centaur_in_med.jpg"), caption: "HMS Centaur in the Mediterranean." },
      { src: W("2/2e/HMS_Centaur_Leaving_Portsmouth.jpg"), caption: "HMS Centaur leaving Portsmouth." },
      { src: W("4/48/HMS_Centaur_1955.jpg"), caption: "HMS Centaur with full deck park, 1955." },
      { src: W("7/79/Comparison_of_angled_flight_decks_of_HMS_Centaur_%28R06%29_and_USS_Antietam_%28CVA-36%29%2C_1955.jpg"), caption: "Centaur's angled flight deck compared with USS Antietam, 1955." },
    ],
    summary:
      "Two years aboard a 22,000-ton fleet carrier flying Sea Vixens, Gannets and helicopters, during the height of Britain's 'East of Suez' commitments.",
    history: [
      "HMS Centaur was the lead ship of her class, laid down at Harland & Wolff in 1944 and commissioned on 1 September 1953 — too late for the Second World War but at the heart of the Cold War navy.",
      "27,000 tons full load, 737 feet long, with an angled flight deck and steam catapults.",
      "During Dad's commission her air group flew de Havilland Sea Vixens (the all-weather fighter), Fairey Gannets (airborne early warning and anti-submarine), and a mix of Westland Whirlwind and Wessex helicopters for plane-guard, search-and-rescue and ASW duty.",
      "In January 1964, Centaur played a pivotal role in the Tanganyika Rifles mutiny — flying 45 Commando Royal Marines ashore by helicopter in the Royal Navy's largest amphibious operation since Suez.",
      "She also conducted operations off Aden and East Africa, and served in the Beira Patrol enforcing sanctions against Rhodesia.",
      "Centaur paid off in August 1965, just before Dad's draft ended — she was the last of her class to operate fixed-wing aircraft.",
    ],
    role_detail:
      "Dad remembers the Sea Vixens, the Gannets and the Whirlwind helicopters thundering off the deck above him — and a second type of helicopter whose name he can't quite recall (almost certainly the Westland Wessex, which embarked in Centaur during his commission). As a young marine engineer rating he worked below all of it, among the boilers, turbines and auxiliary machinery deep beneath the flight deck — long, hot watches in spaces few visitors ever saw, keeping a 27,000-ton carrier and her aircraft moving.",
    airGroup: [
      {
        name: "de Havilland Sea Vixen",
        role: "All-weather carrier-borne fighter",
        image: W("a/a2/De_Havilland_Sea_Vixens_in_1961.jpg"),
        confirmedByDad: true,
        detail: "Twin-boom, two-seat fighter armed with Firestreak and Red Top air-to-air missiles, two 30mm Aden cannon and rocket pods. Centaur flew 893 NAS and 890 NAS Sea Vixens.",
      },
      {
        name: "Fairey Gannet",
        role: "Airborne early warning &amp; ASW",
        image: W("6/63/Fairey_Gannet_AEW3%2C_UK_-_Navy_AN1339874.jpg"),
        confirmedByDad: true,
        detail: "Three-seat turboprop with a huge under-belly radome carrying the AN/APS-20 radar — the fleet's eyes far beyond the carrier's horizon. 849 NAS flights.",
      },
      {
        name: "Westland Whirlwind",
        role: "Plane-guard &amp; search-and-rescue helicopter",
        image: W("5/59/Westland_Whirlwind_HAS.7_XL878_PO-514_771_Sq_Portl_200767_edited-2.jpg"),
        confirmedByDad: true,
        detail: "Royal Navy Whirlwind HAS.7 — a single-rotor helicopter that hovered alongside during flying operations to recover any aircrew who ditched. Pictured here in 1967 with 771 NAS at Portland.",
      },
      {
        name: "Westland Wessex",
        role: "Anti-submarine helicopter",
        image: W("7/7d/Westland_Wessex_HAS.1__XM330_.jpg"),
        confirmedByDad: false,
        guessedByDad: true,
        detail: "Turbine-powered HAS.1 with dunking sonar and homing torpedoes — replaced the Whirlwind for ASW from 1962 onward. Almost certainly the 'other helicopter' Dad remembered but couldn't quite name.",
      },
    ],
    memories: [
      {
        date: "22 November 1963",
        title: "President Kennedy is assassinated",
        body: "Dad was aboard Centaur, only weeks into his first sea draft, when the news came through that JFK had been shot in Dallas. He remembers the moment well — the entire ship's company learning of it together at sea.",
      },
      {
        date: "22 December 1963",
        title: "The Lakonia is on fire",
        body: "The Greek-owned cruise ship SS Lakonia (commonly misspelled 'Laconia') caught fire about 180 miles north of Madeira while sailing from Southampton to the Canary Islands. 128 passengers and crew lost their lives. Centaur was one of the warships diverted to the scene — but by the time she arrived the rescue had largely become a recovery. Her boats and helicopters lifted the dead from the sea.",
        photo: {
          src: `${IMG_BASE}/Dad_SS_Lakonia_Whirlwind977_1963.jpg`,
          caption:
            "SS Lakonia adrift and still smouldering — a Royal Navy Westland Whirlwind (tail number 977) hovering overhead. From Dad's own collection.",
          isDad: true,
        },
      },
      {
        date: "23 – 25 December 1963",
        title: "The old naval burial preparation",
        body: "The bodies recovered from the water were brought aboard Centaur and, in the centuries-old Royal Navy manner, sewn into canvas shrouds — the last stitch driven through the nose, the sailmaker's traditional final check that the man was truly gone. Dad was seventeen.",
      },
      {
        date: "25 December 1963",
        title: "Christmas Day in Gibraltar",
        body: "Centaur landed the dead ashore at Gibraltar on Christmas Day — a sombre Christmas for a seventeen-year-old on his first deployment, and a memory that has stayed with him for sixty-plus years.",
      },
    ],
  },
  {
    slug: "hms-pellew",
    name: "HMS Pellew",
    pennant: "F62",
    dates: "December 1965 – January 1967",
    type: "Blackwood-class (Type 14) Anti-Submarine Frigate",
    location: "Home and Mediterranean Fleets",
    role: "Marine Engineering Mechanic",
    images: [
      { src: W("1/10/HMS_Pellew%2C_1962_%28IWM%29.jpg"), caption: "HMS Pellew in 1962. (Imperial War Museum)" },
      { src: W("c/c9/HMS_Pellew%2C_1967_%28IWM%29.jpg"), caption: "HMS Pellew in 1967 — the final year of Dad's draft. (Imperial War Museum)" },
    ],
    summary:
      "A compact, austere 'Second Rate' anti-submarine frigate built for one purpose: hunting Soviet submarines in the GIUK gap.",
    history: [
      "HMS Pellew (F62), named after Admiral Sir Edward Pellew, 1st Viscount Exmouth, was a Blackwood-class Type 14 anti-submarine frigate.",
      "Launched at Yarrow Shipbuilders on 29 September 1954 and commissioned on 26 July 1956.",
      "At 1,180 tons standard she was a 'utility' frigate — single-shaft, single-boiler, with a stripped-down armament of one twin 40mm Bofors and two triple-barrelled Limbo mortars.",
      "Cheap to build and easy to mass-produce, the Blackwoods were intended to be expendable convoy escorts in a hot war with the Soviet Union.",
      "Pellew was deployed with the Fishery Protection Squadron and the 6th Frigate Squadron before being decommissioned in 1969 and scrapped in 1971.",
    ],
    role_detail:
      "Pellew's single-boiler, single-shaft engineering plant gave junior engineers like Dad intensive hands-on responsibility — there was no redundancy and no margin for error. Excellent training for the demanding postings that followed.",
  },
  {
    slug: "hms-jaguar",
    name: "HMS Jaguar",
    pennant: "F37",
    dates: "January 1967 – August 1967",
    type: "Leopard-class (Type 41) Anti-Aircraft Frigate",
    location: "Persian Gulf / Indian Ocean",
    role: "Marine Engineering Mechanic",
    images: [
      { src: W("f/fd/HMS_Jaguar_F37.jpg"), caption: "HMS Jaguar (F37), Leopard-class anti-aircraft frigate." },
      { src: W("e/e2/HMS_Jaguar%2C_1963_%28IWM%29.jpg"), caption: "HMS Jaguar in 1963. (Imperial War Museum)" },
      { src: W("8/87/Het_fregat_Jaguar_vaart_in_de_haven%2C_Bestanddeelnr_921-6704.jpg"), caption: "HMS Jaguar entering harbour." },
    ],
    summary:
      "A diesel-powered air defence frigate operating during the British withdrawal from Aden — one of the last engagements of empire.",
    history: [
      "HMS Jaguar (F37) was a Leopard-class Type 41 anti-aircraft frigate, launched at William Denny & Brothers on 30 July 1957 and commissioned on 12 December 1959.",
      "2,300 tons standard, she was unusual in being driven by eight Admiralty Standard Range diesels rather than steam turbines — giving long endurance for colonial policing duties.",
      "Her main armament was two twin 4.5-inch Mk.6 turrets controlled by the Type 275 fire-control radar.",
      "Jaguar's 1967 commission coincided with the Aden Emergency and the chaotic British withdrawal from the Crater district — a turbulent moment as the Empire contracted.",
      "She was later sold to Bangladesh in 1978 and served as BNS Ali Haider until 2014.",
    ],
    role_detail:
      "Diesel propulsion was a very different discipline from steam — the 36-cylinder ASR diesels demanded a new set of skills. Dad's seven-month draft broadened his engineering portfolio at exactly the right point in his career.",
  },
  {
    slug: "hms-hampshire",
    name: "HMS Hampshire",
    pennant: "D06",
    dates: "August 1967 – May 1969",
    type: "County-class Guided Missile Destroyer (Batch 1)",
    location: "Worldwide deployments — Far East, Mediterranean",
    role: "Leading Marine Engineering Mechanic",
    images: [
      { src: W("d/df/HMS_Hampshire_%2827516082238%29.jpg"), caption: "HMS Hampshire (D06), County-class guided missile destroyer." },
      { src: W("e/e6/Beleuchtete_Schiffe_an_der_Tirpitzmole_zur_Kieler_Woche_1974_%28Kiel_57.118%29.jpg"), caption: "HMS Hampshire illuminated at Kiel Week, 1974." },
      { src: W("d/da/Open_Ship_zur_Kieler_Woche_1974_%28Kiel_4.365%29.jpg"), caption: "Open Ship aboard HMS Hampshire, Kiel Week 1974." },
    ],
    summary:
      "Aboard one of Britain's first guided-missile destroyers — a 6,200-ton COSAG-powered warship at the cutting edge of the Cold War navy.",
    history: [
      "HMS Hampshire (D06) was the third of the eight County-class destroyers, launched at John Brown & Co. on 16 March 1961 and commissioned on 15 March 1963.",
      "6,200 tons standard, she introduced the Combined Steam and Gas (COSAG) machinery — two steam turbines plus four G6 gas turbines for boost power, making her one of the world's first warships with gas turbine propulsion.",
      "Her primary armament was the Sea Slug Mk.I area air defence missile system, twin 4.5-inch guns, Sea Cat point-defence missiles, and a Wessex anti-submarine helicopter — an entirely new generation of warship.",
      "During Dad's draft, Hampshire deployed to the Far East and Mediterranean, exercising with NATO and Commonwealth navies.",
      "Hampshire was decommissioned in 1976 and broken up at Briton Ferry in 1979.",
    ],
    role_detail:
      "Mastering the COSAG plant — switching between steam and gas turbines on the fly — was one of the most demanding tasks in the fleet. It was here, almost certainly, that Dad earned his Leading Hand's rate and began the path toward Mechanician.",
  },
  {
    slug: "hms-sultan-1969",
    name: "HMS Sultan",
    pennant: "Shore Establishment",
    dates: "May 1969 – May 1972",
    type: "Royal Naval School of Marine Engineering",
    location: "Gosport, Hampshire",
    role: "Engineering Mechanician's Course — Petty Officer",
    images: [
      { src: W("e/ee/HMS_Sultan_Caledonian_Gate_%28geograph_3875349%29.jpg"), caption: "The Caledonian Gate at HMS Sultan, Gosport — the entrance to the Royal Navy's School of Marine Engineering." },
    ],
    summary:
      "Three years ashore at the Royal Navy's centre of marine engineering excellence — qualifying as an Engineering Mechanician on a course equivalent to a three-year senior apprenticeship.",
    history: [
      "HMS Sultan stood up at Gosport in 1956 as the Royal Naval School of Marine Engineering, on the site of the former HMS St. Vincent and Forton Barracks.",
      "It remains one of the largest engineering training establishments in Western Europe, training every Royal Navy marine engineer.",
      "Dad arrived as a Leading Marine Engineering Mechanic, having been recommended for further technical training. He was promoted to Petty Officer at the start of the three-year course.",
      "The Engineering Mechanician's course was the equivalent of a full three-year senior apprenticeship — a level of training rarely matched in civilian engineering. It was emphatically engineering, not aviation: fitting and turning in the workshop, theoretical instruction, and hands-on equipment maintenance across the full range of modern warship systems.",
      "Among the practical work was the kind of job he would later lead at sea — for example a 'top overhaul' of a marine diesel engine, stripping the upper end down to inspect and replace pistons, valves and bearings.",
      "Holders of the Mechanician's rate were considered the technical backbone of the fleet, eligible for the most demanding sea-going appointments and for direct entry to the Chief Petty Officers' mess.",
    ],
    role_detail:
      "Dad joined Sultan as a Leading Marine Engineering Mechanic and was promoted to Petty Officer on day one of the three-year Engineering Mechanician's course. He walked out a fully qualified marine engineering specialist, ready for the demands of the County-class destroyers, the commando carriers and — ultimately — the nuclear submarines.",
  },
  {
    slug: "hms-bulwark",
    name: "HMS Bulwark",
    pennant: "R08",
    dates: "May 1972 – January 1974",
    type: "Centaur-class Commando Carrier ('Rusty B')",
    location: "Mediterranean, Caribbean, Far East",
    role: "Petty Officer Mechanician",
    images: [
      { src: EN("5/56/HMS_Bulwark_%28Commando_carrier%29.jpg"), caption: "HMS Bulwark in her commando carrier configuration." },
      { src: W("a/a4/HMS_Bulwark_%28R08%29_off_Vieques_Island_1973.jpg"), caption: "HMS Bulwark off Vieques Island, 1973 — during Dad's draft." },
      { src: W("5/5f/HMS_Bulwark_%28R08%29_at_Singapore_1958.jpeg"), caption: "HMS Bulwark at Singapore, 1958." },
      { src: W("e/e5/HMS_Bulwark_%28R08%29_at_anchor_in_May_1958.jpg"), caption: "HMS Bulwark at anchor, May 1958." },
      { src: W("1/10/Carriers_Bulwark_Melbourne_and_Philippine_Sea_at_Singapore_1958.jpg"), caption: "Carriers Bulwark, Melbourne and Philippine Sea at Singapore, 1958." },
    ],
    summary:
      "Affectionately nicknamed the 'Rusty B', Bulwark was a 23,000-ton commando carrier delivering Royal Marines from the sea by helicopter.",
    history: [
      "HMS Bulwark was laid down at Harland & Wolff in 1945, completed as a conventional aircraft carrier in 1954, and converted to a commando carrier in 1959 — pioneering the modern concept of vertical assault.",
      "She carried up to 900 Royal Marines of a Commando, with their Wessex and later Sea King helicopters, four landing craft on davits, and full medical facilities — the prototype amphibious helicopter assault ship.",
      "Throughout the early 1970s Bulwark exercised across the Mediterranean and Atlantic, supported the withdrawal from the Far East, and stood ready for any emergency from Belize to Cyprus.",
      "Decommissioned in 1981, briefly recommissioned, and finally scrapped at Cairnryan in 1984.",
    ],
    role_detail:
      "As a Petty Officer Mechanician, Dad would have led a watch in the boiler or engine rooms of one of the most-travelled ships in the fleet — keeping steam up so the Royal Marines could lift off the deck at any moment.",
    airGroup: [
      {
        name: "Westland Sea King",
        role: "Anti-submarine &amp; commando helicopter",
        image: W("3/32/Westland_Sea_King_HAS6%2C_UK_-_Navy_AN1173281.jpg"),
        confirmedByDad: true,
        detail: "The big twin-engine Sea King replaced the Wessex aboard Royal Navy carriers from 1969. Dad was actually invited to apply for Royal Navy helicopter aircrew training while aboard Bulwark and had two acclimatisation flights in a Sea King — but, as he says, heights weren't really his thing.",
      },
    ],
    memories: [
      {
        date: "1972 – 1974, aboard Bulwark",
        title: "Two flights in a Sea King — and a polite no thank you",
        body: "Dad was invited to apply for Royal Navy helicopter aircrew training while serving on Bulwark and was given two acclimatisation test flights in a Westland Sea King. He decided not to progress further — heights, as he discovered up there in the doorway of a twin-engine helicopter, were never really his thing.",
      },
    ],
  },
  {
    slug: "hms-dolphin",
    name: "HMS Dolphin",
    pennant: "Shore Establishment",
    dates: "January 1974 – February 1976",
    type: "Royal Navy Submarine School",
    location: "Fort Blockhouse, Gosport",
    role: "Submarine Qualification",
    images: [
      { src: W("1/19/Davis_Apparatus_Brings_Submarine-trapped_Men_To_Safety._14_December_1942%2C_HMS_Dolphin%2C_at_Gosport%2C_the_Way_To_Safety_For_Britain%27s_Underwater_Fighters_Should_They_Be_Trapped_in_a_Doomed_Submarine_Lies_Through_t_A13878.jpg"), caption: "The Davis Apparatus Submarine Escape Tank at HMS Dolphin, Gosport." },
      { src: W("c/c0/Gosport_-_Fort_Blockhouse_-_geograph.org.uk_-_644531_%28cropped%29.jpg"), caption: "Fort Blockhouse, Gosport — the home of HMS Dolphin and the Submarine Service." },
      { src: W("0/01/Fort_Blockhouse_and_the_entrance_to_Portsmouth_Harbour_-_geograph.org.uk_-_72085.jpg"), caption: "Fort Blockhouse at the entrance to Portsmouth Harbour." },
    ],
    summary:
      "The spiritual home of the Royal Navy Submarine Service — where every British submariner earned his Dolphins.",
    history: [
      "HMS Dolphin was commissioned at Fort Blockhouse, Gosport, in 1904 as the original base of the fledgling Royal Navy Submarine Service.",
      "From here, every Royal Navy submariner of the 20th century received his training, from the petrol-driven Holland boats through to the nuclear-powered Polaris and Trident SSBNs.",
      "The submarine school taught escape (in the famous 100-foot Submarine Escape Training Tank), damage control, and the specialist engineering of pressure hulls, batteries, atmospheric control and nuclear propulsion.",
      "Earning the Dolphins badge was — and remains — one of the proudest moments in any submariner's career.",
      "HMS Dolphin paid off in 1999 when the Submarine School moved to HMS Raleigh.",
    ],
    role_detail:
      "A two-year stretch at Dolphin points to specialist nuclear marine engineering training — the deep knowledge required to take charge of a watch in the Manoeuvring Room of a Polaris submarine.",
  },
  {
    slug: "hms-renown",
    name: "HMS Renown",
    pennant: "S26",
    dates: "February 1976 – June 1977",
    type: "Resolution-class Polaris Ballistic Missile Submarine (SSBN)",
    location: "North Atlantic — Deterrent Patrol",
    role: "Chief Petty Officer Mechanician First Class",
    isSubmarine: true,
    images: [
      { src: EN("c/c6/HMS_Renown_%28Resolution-class_submarine%29_.jpg"), caption: "HMS Renown (S26), Resolution-class Polaris ballistic missile submarine." },
    ],
    summary:
      "The pinnacle of a Royal Navy engineer's career — Chief of the Watch on a nuclear-powered, nuclear-armed strategic deterrent submarine.",
    history: [
      "HMS Renown (S26) was the third of four Resolution-class submarines, launched at Cammell Laird on 25 February 1967 and commissioned on 15 November 1968.",
      "7,500 tons submerged, 425 feet long, powered by a single Rolls-Royce PWR-1 pressurised water reactor driving English Electric geared turbines — capable of more than 25 knots dived and effectively unlimited endurance.",
      "Armed with sixteen Polaris A-3T submarine-launched ballistic missiles, each carrying three 200-kiloton warheads — the United Kingdom's continuous-at-sea nuclear deterrent.",
      "The Resolution class kept at least one boat on patrol every day from 1969, an unbroken vigil that still continues with their Vanguard- and Dreadnought-class successors.",
      "Renown completed her 50th deterrent patrol in 1992 and was decommissioned in 1996.",
    ],
    role_detail:
      "It was aboard Renown that Dad reached the apex of his service as Chief Petty Officer Mechanician First Class — one of the most senior technical ratings in the Royal Navy. He took watches in the Manoeuvring Room of a nuclear submarine on deterrent patrol: an extraordinary responsibility, almost unknown to the public, and central to the security of the realm.",
  },
  {
    slug: "hms-sultan-1977",
    name: "HMS Sultan",
    pennant: "Shore Establishment",
    dates: "June 1977 – March 1978",
    type: "Royal Naval School of Marine Engineering",
    location: "Gosport, Hampshire",
    role: "Final Draft — Resettlement",
    images: [
      { src: W("e/ee/HMS_Sultan_Caledonian_Gate_%28geograph_3875349%29.jpg"), caption: "HMS Sultan, Gosport — where Dad's Mechanician training began, and where his Royal Navy career ended." },
    ],
    summary:
      "A final draft back to Sultan, completing fifteen-and-a-half years of unbroken service — and Dad's own words for what came next: 'released into the real world.'",
    history: [
      "Dad returned to where his Mechanician's training began — closing the circle of a Royal Navy career that ran from 1962 to 1978.",
      "By 1978, Sultan was preparing the next generation of marine engineers for the steam-and-gas-turbine warships and nuclear submarines that would carry the Royal Navy into the 1980s and the Falklands.",
      "After completing his final draft Dad took his discharge — in his own words, 'released into the real world.'",
    ],
    role_detail:
      "A final tour as senior rating, almost certainly involving the training of newer engineers — passing on the hard-won knowledge of sixteen years at sea and ashore before stepping out of uniform.",
  },
];

export const SHIP_BY_SLUG = Object.fromEntries(SHIPS.map((s) => [s.slug, s]));

// Flat list of every image across every ship, for the gallery
export const ALL_IMAGES = SHIPS.flatMap((s) =>
  s.images.map((img, i) => ({
    ...img,
    ship: s.name,
    pennant: s.pennant,
    dates: s.dates,
    slug: s.slug,
    isHero: i === 0,
  }))
);
